<template>
	<div class="relative min-h-0">
		<!-- Offline Status Alert at top of table -->
		<div v-if="!store.isOnline || store.queueLength > 0" class="mb-4">
			<div
:class="[
				'px-4 py-3 flex items-center justify-between gap-4 rounded-lg border',
				store.isOnline ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
			]">
				<div class="flex items-center gap-3">
					<div v-if="!store.isOnline" class="flex items-center gap-2">
						<svg class="w-5 h-5 text-red-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
							<path
fill-rule="evenodd"
								d="M13.477 14.89A6 6 0 0 1 5.11 2.523a6 6 0 0 1 8.367 8.367zM9 13a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0-5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
								clip-rule="evenodd" />
						</svg>
						<span class="text-sm font-semibold text-red-700">You are offline</span>
						<span class="text-xs text-red-600">You can edit item counts, but cannot add or change
							filters</span>
					</div>

					<div v-else-if="store.queueLength > 0" class="flex items-center gap-2">
						<div class="flex items-center gap-1">
							<svg
class="w-5 h-5 text-amber-600 animate-spin" fill="none" stroke="currentColor"
								viewBox="0 0 24 24">
								<path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
						</div>
						<span class="text-sm font-semibold text-amber-700"> Syncing changes </span>
						<span class="text-xs text-amber-600">
							{{ store.queueLength }}
							{{ store.queueLength === 1 ? 'item' : 'items' }} pending
						</span>
					</div>
				</div>

				<div v-if="!store.isOnline" class="text-xs text-red-600 font-medium">
					Waiting for connection...
				</div>
				<div v-else-if="store.queueLength > 0" class="text-xs text-amber-600 font-medium">
					{{ store.isSyncing ? 'Syncing...' : 'Ready to sync' }}
				</div>
			</div>
		</div>
		<!-- Datatable Component -->
		<datatable
v-if="!store.showVariance" :headers="headers" :value="items" :loading="isloading"
			:total="store.totalItems" :rows="25" :rows-per-page-options="[10, 25, 50, 100]" paginator sortable clickable
			paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown JumpToPageDropdown"
			@update:server-options="
				(params) =>
					store.loadItems(
						true,
						params.page,
						params.rowsPerPage,
						params.sortBy,
						params.sortType
					)
			">
			<template #row-Status="{ row }">
				<div class="flex items-center justify-center gap-1">
					<span
v-if="row.Status !== '0'" class="inline-block h-6 w-6 rounded-full" :class="{
						'bg-yellow-500': row.Status == '1',
						'bg-green-600': row.Status == '2',
						'bg-red-600': row.Status == '4',
						'bg-blue-600': row.Status == '5'
					}"></span>
				</div>
			</template>

			<template #row-Quantity="{ row }">
				<input
v-model.number="row.Quantity" type="number" min="0" :class="[
					'px-1 py-0.5 bg-gray-100 border rounded text-right w-20 ' +
					'focus:ring-2 focus:ring-blue-400 focus:bg-white',
					rowErrors[row.id] ? 'border-red-500' : 'border-gray-300'
				]" @input="rowChanged(row)" />
			</template>

			<template #row-Recount="{ row }">
				<input
v-model.number="row.Recount" type="number" min="0" :class="[
					'px-1 py-0.5 bg-gray-100 border rounded text-right w-20 ' +
					'focus:ring-2 focus:ring-blue-400 focus:bg-white',
					rowErrors[row.id] ? 'border-red-500' : 'border-gray-300'
				]" @input="rowChanged(row)" />
			</template>

			<template #row-AddStack="{ row }">
				<button
:disabled="!store.isOnline"
					class="bg-mabey-950 text-white px-3 py-1.5 rounded hover:bg-mabey-900 transition-colors duration-200 text-sm shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-mabey-700 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
					title="Add this item" @click="addItem(row)">
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
					</svg>
				</button>
			</template>
		</datatable>

		<!-- Variance Datatable Component -->
		<datatable
v-else :headers="vheaders" :value="items" :loading="isloading" :total="store.totalItems" :rows="25"
			:rows-per-page-options="[10, 25, 50, 100]" paginator sortable clickable
			paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown JumpToPageDropdown"
			@update:server-options="
				(params) =>
					store.loadItems(
						true,
						params.page,
						params.rowsPerPage,
						params.sortBy,
						params.sortType
					)
			">
			<template #row-Status="{ row }">
				<div class="flex items-center justify-center gap-1">
					<span
v-if="row.Status !== '0'" class="inline-block h-6 w-6 rounded-full" :class="{
						'bg-yellow-500': row.Status == '1',
						'bg-green-600': row.Status == '2',
						'bg-red-600': row.Status == '4',
						'bg-blue-600': row.Status == '5'
					}"></span>
				</div>
			</template>

			<template #row-ItemCost="{ row }">
				{{

					canViewVariance()
						? Math.abs(
							(getTotalByItemNo(row.ITEMNO) - Number(row.ExpCount ?? 0)) *
							Number(row.ItemCost ?? 0)
						).toLocaleString('en-GB', {
							style: 'currency',
							currency: 'GBP'
						})
						: ''
				}}
			</template>

			<template #row-ExpCount="{ row }">
				{{
					canViewVariance()
						? getTotalByItemNo(row.ITEMNO) - Number(row.ExpCount ?? 0)
						: ''
				}}
			</template>

			<template #row-Quantity="{ row }">
				<input
v-model.number="row.Quantity" type="number" min="0" :class="[
					'px-1 py-0.5 bg-gray-100 border rounded text-right w-20 ' +
					'focus:ring-2 focus:ring-blue-400 focus:bg-white',
					rowErrors[row.id] ? 'border-red-500' : 'border-gray-300'
				]" @input="rowChanged(row)" />
			</template>

			<template #row-Recount="{ row }">
				<input
v-model.number="row.Recount" type="number" min="0" :class="[
					'px-1 py-0.5 bg-gray-100 border rounded text-right w-20 ' +
					'focus:ring-2 focus:ring-blue-400 focus:bg-white',
					rowErrors[row.id] ? 'border-red-500' : 'border-gray-300'
				]" @input="rowChanged(row)" />
			</template>
			<template #row-Confirmed="{ row }">
				<div class="flex items-center justify-center py-1 px-3">
					<app-checkbox
v-model="row.Confirmed" name="confirm" wrapper="" :true-value="true"
						:false-value="false" @change="rowChanged(row)" />
				</div>
			</template>

			<template #row-AddStack="{ row }">
				<button
:disabled="!store.isOnline"
					class="bg-mabey-950 text-white px-3 py-1.5 rounded hover:bg-mabey-900 transition-colors duration-200 text-sm shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-mabey-700 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
					title="Add this item" @click="addItem(row)">
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
					</svg>
				</button>
			</template>
		</datatable>

		<!-- Clone Item Form Slide-out Panel -->
		<CloneItemForm :open="showCloneForm" :item="selectedCloneItem" @close="showCloneForm = false" />

		<!-- Page-level saving overlay -->
		<div v-if="isSaving" class="fixed inset-0 flex items-center justify-center z-50">
			<div class="h-8 w-8 rounded-full border-4 border-gray-300 border-t-blue-500 animate-spin"></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { StockItem } from '@/data/schema/stocktake'
import type { Header } from '@/data/types/datatable'
import { ref, onBeforeUnmount, onMounted, type Ref } from 'vue'
import useStockTake from '@/stores/stocktakestore'
import { storeToRefs } from 'pinia'
import debounce from 'lodash/debounce'
import isBlankNumeric from '@/utils/stocktakeapp'
import { nanoid } from 'nanoid'
import CloneItemForm from './CloneItemForm.vue'
import useToasterStore from '@/stores/toaster'

const store = useStockTake()
const { items, isloading } = storeToRefs(store)
const isSaving = ref(false)

const rowErrors: Ref<Record<string | number, string>> = ref({})
const toaster = useToasterStore()

const syncing = ref(false)

// Clone form variables
const showCloneForm = ref(false)
const selectedCloneItem = ref<StockItem | null>(null)


// Define headers for datatable
const headers = ref<Header[]>([
	{ value: 'Status', text: 'Status', props: { sortable: true, width: 80 } },
	{ value: 'STACKNO', text: 'Stack', props: { sortable: true } },
	{ value: 'PGROUP', text: 'P Group', props: { sortable: true } },
	{ value: 'GRPGROUP', text: 'Sub Group', props: { sortable: true } },
	{ value: 'ITEMNO', text: 'Item', props: { sortable: true } },
	{ value: 'MEMO', text: 'Description', props: { sortable: true } },
	{ value: 'AREA', text: 'Area', props: { sortable: true } },
	{ value: 'Quantity', text: 'Count', props: { sortable: true } },
	{ value: 'Recount', text: 'Recount', props: { sortable: true } },
	{ value: 'AddStack', text: 'Add Stack', props: { sortable: false } }
])

// Define headers for datatable
const vheaders = ref<Header[]>([
	{ value: 'Status', text: 'Status', props: { sortable: true, width: 80 } },
	{ value: 'STACKNO', text: 'Stack', props: { sortable: true } },
	{ value: 'ITEMNO', text: 'Item', props: { sortable: true } },
	{ value: 'MEMO', text: 'Description', props: { sortable: true } },
	{ value: 'AREA', text: 'Area', props: { sortable: true } },
	{ value: 'ExpCount', text: 'Quantity Variance', props: { sortable: false } },
	{ value: 'ItemCost', text: 'Cost Variance', props: { sortable: false } },
	{ value: 'Quantity', text: 'Count', props: { sortable: true } },
	{ value: 'Recount', text: 'Recount', props: { sortable: true } },
	{ value: 'Confirmed', text: 'Confirm', props: { sortable: false } },
	{ value: 'AddStack', text: 'Add Stack', props: { sortable: false } }
])

const getTotalByItemNo = (itemno: string): number => {
	return items.value.reduce((total, item) => {
		if (item.ITEMNO !== itemno) return total

		const value = Number(item.Recount ?? item.Quantity ?? 0)
		return total + (isNaN(value) ? 0 : value)
	}, 0)
}

// Track last saved values to avoid unnecessary API calls
const lastSaved = ref<
	Record<string | number, { countQty: number | null; recountQty: number | null }>
>({})

function updateOnlineStatus() {
	const online = navigator.onLine

	if (online) {
		store.startPolling()
		syncQueue()
	} else {
		store.pausePolling()
	}
}

const saveRow = async (item: StockItem, isFromSyncQueue = false) => {
	delete rowErrors.value[item.id]
	isSaving.value = true

	const depot = store.selectedDepot.prefix.toUpperCase()

	const previous = lastSaved.value[item.id] || {
		countQty: null,
		recountQty: null
	}

	const countChanged = item.Quantity !== previous.countQty
	const recountChanged = item.Recount !== previous.recountQty

	if (!countChanged && !recountChanged && item.Status < '4') {
		isSaving.value = false
		return
	}

	if (isBlankNumeric(item.Quantity) && item.Quantity) {
		isSaving.value = false
		const errorMessage = 'Quantity must be numeric.'
		toaster.error({ text: errorMessage })
		return
	}
	if (isBlankNumeric(item.Recount) && item.Recount) {
		isSaving.value = false
		const errorMessage = 'Recount must be numeric.'
		toaster.error({ text: errorMessage })
		return
	}

	const withoutNulls = {
		...Object.fromEntries(
			Object.entries(item).filter(([k, v]) => v !== null && k !== 'id' && k !== 'STACKNO')
		),
		Status: !item.Confirmed || item.Confirmed === 0 ? '1' : '5',
		AREA: store.enteredArea,
		Variance:
			Number(
				item.Recount !== null && item.Recount !== undefined
					? item.Recount
					: item.Quantity
			) - Number(item.ExpCount),
		First_Counter: store.counter1 || '',
		Second_Counter: store.counter2 || ''
	}

	const withoutNullsForQueue = {
		...Object.fromEntries(
			Object.entries(item).filter(([k, v]) => v !== null && k !== 'STACKNO')
		),
		Status: !item.Confirmed || item.Confirmed === 0 ? '1' : '5',
		AREA: store.enteredArea,
		Variance:
			Number(
				item.Recount !== null && item.Recount !== undefined
					? item.Recount
					: item.Quantity
			) - Number(item.ExpCount),
		First_Counter: store.counter1 || '',
		Second_Counter: store.counter2 || ''
	}

	const queueId = crypto?.randomUUID?.() ?? nanoid()

	// For new offline items, queue as update not clone
	const isNewOfflineItem = store.isNewOfflineItem(item.id)
	const queuedAction = {
		queueId,
		action: 'update' as const,
		depot: String(depot),
		itemId: item.id,
		payload: withoutNullsForQueue,
		timestamp: Date.now()
	}

	if (store.isOnline) {
		try {
			await store.setSyncingItem(item.id, true)
			await store.updateStockItem(String(depot), String(item.id), withoutNulls)

			lastSaved.value[item.id] = {
				countQty: item.Quantity,
				recountQty: item.Recount
			}

			isSaving.value = false
			await store.setSyncingItem(item.id, false)

			// Only reload table if online
			if (store.isOnline) {
				await store.loadItems(true)
			}

			return
		} catch (err: unknown) {
			const message =
				(err as any)?.response?.data?.message ||
				(err as Error)?.message ||
				'Failed to save. Please try again.'

			toaster.error({ text: message })

			item.Quantity = previous.countQty
			item.Recount = previous.recountQty

			isSaving.value = false
			await store.setSyncingItem(item.id, false)
		}
	} else {
		try {
			if (!isFromSyncQueue) {
				await store.addToOfflineQueue(queuedAction)
				if (!isNewOfflineItem) {
					toaster.success({
						text: 'Change saved offline. Will sync when online.'
					})
				}
			}

			lastSaved.value[item.id] = {
				countQty: item.Quantity,
				recountQty: item.Recount
			}

			isSaving.value = false
			return
		} catch (err: unknown) {
			const message =
				(err as Error)?.message || 'Failed to save offline. Please try again.'

			toaster.error({ text: message })

			item.Quantity = previous.countQty
			item.Recount = previous.recountQty

			isSaving.value = false
		}
	}
}

const rowChanged = debounce((item: StockItem) => {
	void saveRow(item)
}, 1000)

const addItem = (item: StockItem) => {
	if (!store.isOnline) return

	// Open the clone form with the selected item
	selectedCloneItem.value = item
	showCloneForm.value = true
}

const canViewVariance = () => {
	return true
	// return userStore.isAbleTo(['view-stock-variance'])
}

async function syncQueue() {
	if (syncing.value || !store.isOnline) return
	syncing.value = true
	store.setIsSyncing(true)
	isSaving.value = true

	try {
		const queue = (await store.getOfflineQueue?.()) ?? []
		if (!queue.length) {
			isSaving.value = false
			return
		}

		const tempIdMap = new Map<string, number>()

		for (const action of queue) {
			try {
				if (action.action === 'update' && action.itemId) {
					const itemId = action.itemId

					await store.setSyncingItem(itemId, true)

					await store.updateStockItem(action.depot, String(itemId), action.payload)
					await store.removeFromOfflineQueue(action.queueId)
					await store.setSyncingItem(itemId, false)
				} else if (action.action === 'clone') {
					const result = await store.duplicateStockItem(action.depot, action.payload)

					if (result?.data?.id && action.itemId) {
						tempIdMap.set(String(action.itemId), result.data.id)
					}

					await store.removeFromOfflineQueue(action.queueId)
				}
			} catch (err: any) {
				const message =
					(err as any)?.response?.data?.message ||
					(err as Error)?.message ||
					'Failed to sync item'
				toaster.error({ text: message })
				await store.removeFromOfflineQueue(action.queueId)
				if (action.itemId) {
					await store.setSyncingItem(action.itemId, false)
				}
				console.error('Failed to sync action:', action, err)
			}
		}

		if (tempIdMap.size > 0) {
			tempIdMap.forEach((serverId, tempId) => {
				const itemIndex = store.items.findIndex((i) => i.id === Number(tempId))
				if (itemIndex >= 0) {
					store.items[itemIndex].id = serverId
					store.removeNewOfflineItem(tempId)
					lastSaved.value[serverId] = lastSaved.value[tempId]
					delete lastSaved.value[tempId]
				}
			})
		}

		// Only reload table if we're still online
		if (navigator.onLine) {
			await store.loadItems(true)
		}
	} finally {
		syncing.value = false
		store.setIsSyncing(false)
		isSaving.value = false
	}
}

onMounted(async () => {
	window.addEventListener('online', updateOnlineStatus)
	window.addEventListener('offline', updateOnlineStatus)

	// Initialize queue length from store
	await store.updateQueueLength()

	// Sync queue if online
	if (store.isOnline) {
		await syncQueue()
	}
})

onBeforeUnmount(() => {
	rowChanged.cancel()

	window.removeEventListener('online', updateOnlineStatus)
	window.removeEventListener('offline', updateOnlineStatus)
})
</script>
